import React, { useState, useEffect, useRef } from 'react';
import TableRow from './TableRow';

/**
 * Table component that renders tabular data with customizable columns
 *
 * @param {Object} props Component props
 * @param {Array} props.data Array of objects to display in the table
 * @param {Array} props.columns Column definitions array with objects: { key, header, render, sortable, width }
 * @param {string} props.keyField Field name to use as unique identifier for rows (default: 'id')
 * @param {boolean} props.sortable Whether the table should support sorting (default: true)
 * @param {boolean} props.pagination Whether to enable pagination (default: false)
 * @param {number} props.itemsPerPage Number of items to show per page when pagination is enabled (default: 10)
 * @param {boolean} props.selectable Whether rows can be selected (default: false)
 * @param {Function} props.onRowClick Callback function when a row is clicked (row, index) => {}
 * @param {Function} props.onSelectionChange Callback when selected rows change (selectedRows) => {}
 * @param {boolean} props.draggable Whether rows can be dragged and dropped for reordering (default: false)
 * @param {Function} props.onDragEnd Callback when drag and drop reordering is complete (newData) => {}
 * @param {string} props.className Additional CSS class name
 * @param {Object} props.style Additional inline styles
 */
export default function Table({
                                  data = [],
                                  columns = [],
                                  keyField = 'id',
                                  sortable = true,
                                  pagination = false,
                                  itemsPerPage = 10,
                                  selectable = false,
                                  onRowClick = null,
                                  onSelectionChange = null,
                                  className = '',
                                  style = {},
                                  draggable = false,
                                  onDragEnd = null
                              }) {
    // State for sorting
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });

    // State for pagination
    const [currentPage, setCurrentPage] = useState(1);

    // State for row selection
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // State for drag and drop
    const [draggedRow, setDraggedRow] = useState(null);
    const [dropIndicatorVisible, setDropIndicatorVisible] = useState(false);
    const [dropPosition, setDropPosition] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const tableBodyRef = useRef(null);
    const dropIndicatorRef = useRef(null);

    // Reset pagination when data changes
    useEffect(() => {
        setCurrentPage(1);
    }, [data]);

    // Reset selection when data changes
    useEffect(() => {
        setSelectedRows([]);
        setSelectAll(false);
    }, [data]);

    // Notify selection changes
    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(selectedRows);
        }
    }, [selectedRows, onSelectionChange]);

    // Handle sort requests
    const requestSort = (key) => {
        if (!sortable) return;

        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Sort data based on current sortConfig
    const sortedData = React.useMemo(() => {
        if (!sortConfig.key || !sortable) return data;

        return [...data].sort((a, b) => {
            if (a[sortConfig.key] === null) return 1;
            if (b[sortConfig.key] === null) return -1;
            if (a[sortConfig.key] === b[sortConfig.key]) return 0;

            const comparison = a[sortConfig.key] > b[sortConfig.key] ? 1 : -1;
            return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
    }, [data, sortable, sortConfig.key, sortConfig.direction]);

    // Apply pagination
    const paginatedData = React.useMemo(() => {
        if (!pagination) return sortedData;

        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedData.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedData, pagination, currentPage, itemsPerPage]);

    // Calculate total pages
    const totalPages = React.useMemo(() => {
        if (!pagination) return 1;
        return Math.ceil(sortedData.length / itemsPerPage);
    }, [pagination, itemsPerPage, sortedData.length]);

    // Handle page changes
    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages) return;
        setCurrentPage(newPage);
    };

    // Handle row selection
    const handleRowSelect = (rowKey, isSelected) => {
        if (!selectable) return;

        setSelectedRows(prev => {
            if (isSelected) {
                return [...prev, rowKey];
            } else {
                return prev.filter(key => key !== rowKey);
            }
        });
    };

    // Handle select all
    const handleSelectAll = (isSelected) => {
        if (!selectable) return;

        setSelectAll(isSelected);
        if (isSelected) {
            // Select all items on the current page if paginated
            const keys = paginatedData.map(row => row[keyField]);
            setSelectedRows(keys);
        } else {
            setSelectedRows([]);
        }
    };

    // Generate sort indicator
    const getSortIndicator = (key) => {
        if (!sortable || sortConfig.key !== key) return null;

        return (
            <span className="sort-indicator">
        {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
      </span>
        );
    };

    // Drag and drop handlers
    const handleDragStart = (e, row, rowIndex) => {
        if (!draggable) return;

        setDraggedRow({ row, rowIndex });
        setIsDragging(true);
        setDropIndicatorVisible(true);

        // Set drag data
        const dragData = {
            rowKey: row[keyField],
            rowIndex: rowIndex
        };

        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    };

    const handleDragEnd = (e) => {
        if (!draggable) return;

        setDraggedRow(null);
        setIsDragging(false);
        setDropIndicatorVisible(false);
        setDropPosition(null);
    };

    const findClosestRow = (rows, targetY) => {
        return rows.reduce((closest, row) => {
            const rowElement = row.ref?.current;
            if (!rowElement) return closest;

            const rowRect = rowElement.getBoundingClientRect();
            const rowY = rowRect.top + (rowRect.height / 2);
            const distance = Math.abs(targetY - rowY);

            if (distance < closest.distance) {
                const direction = targetY <= rowY ? 'above' : 'below';
                return { row, distance, direction, element: rowElement };
            }
            return closest;
        }, { row: null, distance: Number.MAX_VALUE, direction: 'above', element: null });
    };

    const handleDragOver = (e) => {
        if (!draggable || !isDragging) return;

        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        const tableBody = tableBodyRef.current;
        if (!tableBody) return;

        const allRows = Array.from(tableBody.querySelectorAll('tr[draggable="true"]'));
        const rowRefs = allRows.map(element => ({
            ref: { current: element },
            element
        }));

        const closest = findClosestRow(rowRefs, e.clientY);

        if (closest.row && dropIndicatorRef.current) {
            const dropIndicator = dropIndicatorRef.current;

            if (closest.direction === 'above') {
                closest.element.parentNode.insertBefore(dropIndicator, closest.element);
            } else {
                if (closest.element.nextSibling) {
                    closest.element.parentNode.insertBefore(dropIndicator, closest.element.nextSibling);
                } else {
                    closest.element.parentNode.appendChild(dropIndicator);
                }
            }

            setDropPosition({
                targetRow: closest.element,
                direction: closest.direction
            });
        }
    };

    const handleDrop = (e) => {
        if (!draggable || !draggedRow) return;

        e.preventDefault();

        const tableBody = tableBodyRef.current;
        if (!tableBody || !dropPosition) return;

        // Get the current order of rows
        const currentData = paginatedData;
        const draggedRowData = draggedRow.row;
        const draggedIndex = draggedRow.rowIndex;

        // Find the target index based on drop position
        const allRows = Array.from(tableBody.querySelectorAll('tr[draggable="true"]'));
        const targetElement = dropPosition.targetRow;
        const targetIndex = allRows.indexOf(targetElement);

        if (targetIndex === -1 || draggedIndex === targetIndex) {
            // Reset state if invalid drop
            handleDragEnd(e);
            return;
        }

        // Calculate new position
        let newIndex = targetIndex;
        if (dropPosition.direction === 'below') {
            newIndex = targetIndex + 1;
        }

        // Adjust for the dragged row being removed
        if (draggedIndex < newIndex) {
            newIndex--;
        }

        // Create new data array with reordered items
        const newData = [...currentData];
        const [removed] = newData.splice(draggedIndex, 1);
        newData.splice(newIndex, 0, removed);

        // Clean up drag state
        handleDragEnd(e);

        // Notify parent of the change
        if (onDragEnd) {
            onDragEnd(newData);
        }
    };

    const handleDragEnter = (e) => {
        if (!draggable) return;
        e.preventDefault();
    };

    // Use current data for display
    const displayData = paginatedData;

    // Render drop indicator row
    const renderDropIndicator = () => {
        if (!draggable || !dropIndicatorVisible) return null;

        return (
            <tr
                ref={dropIndicatorRef}
                className="drop-indicator"
                style={{
                    backgroundColor: '#e3f2fd',
                    border: '2px dashed #2196f3',
                    height: '4px'
                }}
            >
                <td colSpan={columns.length + (selectable ? 1 : 0) + (draggable ? 1 : 0)}>
                    <div style={{
                        height: '2px',
                        backgroundColor: '#2196f3',
                        margin: '1px 0'
                    }} />
                </td>
            </tr>
        );
    };

    // Render table header
    const renderTableHeader = () => (
        <thead>
        <tr>
            {selectable && (
                <th className="select-column">
                    <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={e => handleSelectAll(e.target.checked)}
                    />
                </th>
            )}
            {columns.map((column) => (
                <th
                    key={column.key}
                    onClick={() => column.sortable !== false && requestSort(column.key)}
                    className={`
              ${column.sortable !== false && sortable ? 'sortable-column' : ''} 
              ${sortConfig.key === column.key ? 'sorted' : ''}
            `}
                    style={column.width ? { width: column.width } : {}}
                >
                    {column.header || column.key}
                    {getSortIndicator(column.key)}
                </th>
            ))}
        </tr>
        </thead>
    );

    // Render table body
    const renderTableBody = () => (
        <tbody
            ref={tableBodyRef}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnter={handleDragEnter}
        >
        {displayData.length === 0 ? (
            <tr className="no-data">
                <td colSpan={columns.length + (selectable ? 1 : 0)}>No data available</td>
            </tr>
        ) : (
            <>
                {displayData.map((row, rowIndex) => {
                    if (!row) return null;
                    const rowKey = row[keyField];
                    const isSelected = selectedRows.includes(rowKey);
                    const isRowDragging = draggedRow?.row === row;

                    return (
                        <TableRow
                            key={rowKey || rowIndex}
                            row={row}
                            rowIndex={rowIndex}
                            columns={columns}
                            keyField={keyField}
                            selectable={selectable}
                            isSelected={isSelected}
                            handleRowSelect={handleRowSelect}
                            onRowClick={onRowClick}
                            draggable={draggable}
                            isDragging={isRowDragging}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                        />
                    );
                })}
                {renderDropIndicator()}
            </>
        )}
        </tbody>
    );

    // Render pagination controls
    const renderPagination = () => {
        if (!pagination || totalPages <= 1) return null;

        return (
            <div className="pagination-controls">
                <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                >
                    First
                </button>
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    Previous
                </button>
                <span className="page-info">
          Page {currentPage} of {totalPages}
        </span>
                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    Next
                </button>
                <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                >
                    Last
                </button>
            </div>
        );
    };


    return (
        <div className={`table-container ${className}`} style={style}>
            <table className="data-table">
                {renderTableHeader()}
                {renderTableBody()}
            </table>
            {renderPagination()}

            {pagination && (
                <div className="table-info">
                    Showing {paginatedData.length} of {data.length} items
                </div>
            )}
        </div>
    );
}
